'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

// ── Typing rhythm ─────────────────────────────────────────────────────
// Returns a human-like delay (ms) before the next character is rendered.
// Punctuation triggers longer pauses to mimic natural reading rhythm.
function typingDelay(char: string): number {
  if ('!?'.includes(char))  return 90  + Math.random() * 60;   // 90–150 ms
  if (char === '.')         return 70  + Math.random() * 50;   // 70–120 ms
  if (char === '\n')        return 60  + Math.random() * 40;   // 60–100 ms
  if (',;:'.includes(char)) return 35  + Math.random() * 25;   // 35–60  ms
  if (char === ' ')         return 18  + Math.random() * 12;   // 18–30  ms
  return                           6   + Math.random() * 6;    // 6–12   ms  (normal)
}
import { MessageBubble }    from './MessageBubble';
import { QuickSuggestions } from './QuickSuggestions';
import { TypingIndicator }  from './TypingIndicator';
import { ChatRating }       from './ChatRating';
import { useChatSounds }    from './useChatSounds';
import { useVisitorMemory, detectSource } from './useVisitorMemory';
import { NewPostNotifyButton } from '@/components/blog/NewPostNotifyButton';
import { getStoredVisitorId } from '@/lib/utils/tracking';
import type { ChatMessage, ChatAttachment } from '@/types';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/portfolio/api/v1';
const CHAT_URL = `${API_BASE}/chatbot/message`;
const RATE_URL = (sid: string) => `${API_BASE}/chatbot/${sid}/rate`;
const FEEDBACK_URL = (sid: string) => `${API_BASE}/chatbot/${sid}/message-feedback`;
const SESSION_URL = (sid: string) => `${API_BASE}/chatbot/sessions/${sid}`;
const VISITOR_SESSIONS_URL = (vid: string) => `${API_BASE}/chatbot/visitor/${vid}/sessions`;

interface SessionSummary {
  sessionId:   string;
  lang:        string;
  visitorName: string | null;
  messageCount: number;
  preview:     string;
  lastMessage: string;
  updatedAt:   string;
}

interface ChatWindowProps {
  locale:  string;
  onClose: () => void;
}

// ── Localisation ──────────────────────────────────────────────────────
const T = {
  en: {
    title:          'Zain',
    subtitle:       'AI assistant · Usually replies instantly',
    placeholder:    'Type a message…',
    send:           'Send',
    close:          'Close chat',
    attach:         'Attach image, audio or file',
    removeAtt:      'Remove attachment',
    welcomeTitle:   'Hi! I\'m Zain 👋',
    welcomeDesc:    'Mohamed\'s AI assistant. Ask me about his work, hire him, or just say hello.',
    welcomeBack:    (name: string | null) =>
                      `Welcome back${name ? ', ' + name : ''}! Ready to continue where we left off?`,
    bookCall:       'Chat on WhatsApp →',
    newChat:        'New conversation',
    hint:           'Enter to send · Shift+Enter for new line',
    namePrompt:     'What should I call you?',
    namePlaceholder:'Your name (optional)',
    nameSave:       'Save',
    nameSkip:       'Skip',
    history:        'Previous chats',
    historyEmpty:   'No previous conversations yet.',
    historyTitle:   'Your previous chats',
    back:           'Back',
    messages:       'messages',
    fbUp:           'Good response',
    fbDown:         'Bad response',
    fbThanks:       'Thanks for the feedback!',
    retry:          'Retry',
  },
  ar: {
    title:          'زين',
    subtitle:       'مساعد ذكي · يرد فوراً',
    placeholder:    'اكتب رسالة…',
    send:           'إرسال',
    close:          'إغلاق',
    attach:         'إرفاق صورة أو صوت أو ملف',
    removeAtt:      'إزالة المرفق',
    welcomeTitle:   'مرحباً! أنا زين 👋',
    welcomeDesc:    'مساعد محمد الذكي. اسألني عن أعماله، وظّفه، أو قل مرحباً.',
    welcomeBack:    (name: string | null) =>
                      `أهلاً بعودتك${name ? ' يا ' + name : ''}! نكمل من حيث توقفنا؟`,
    bookCall:       'تواصل عبر واتساب →',
    newChat:        'محادثة جديدة',
    hint:           'Enter للإرسال · Shift+Enter لسطر جديد',
    namePrompt:     'بماذا أناديك؟',
    namePlaceholder:'اسمك (اختياري)',
    nameSave:       'حفظ',
    nameSkip:       'تخطّي',
    history:        'المحادثات السابقة',
    historyEmpty:   'لا توجد محادثات سابقة بعد.',
    historyTitle:   'محادثاتك السابقة',
    back:           'رجوع',
    messages:       'رسائل',
    fbUp:           'رد جيد',
    fbDown:         'رد سيئ',
    fbThanks:       'شكراً لملاحظتك!',
    retry:          'إعادة المحاولة',
  },
} as const;

// ── Source-aware welcome chip labels ─────────────────────────────────
const SOURCE_CHIP: Record<string, { en: string; ar: string }> = {
  linkedin:   { en: 'Hire me for your team',      ar: 'وظّفني لفريقك'           },
  github:     { en: 'Let\'s talk tech',           ar: 'نتحدث عن التقنية'       },
  google:     { en: 'What do you need built?',    ar: 'ماذا تريد أن تبني؟'     },
  upwork:     { en: 'Start a project',            ar: 'ابدأ مشروعاً'            },
  freelancer: { en: 'Start a project',            ar: 'ابدأ مشروعاً'            },
  twitter:    { en: 'Tell me about your idea',    ar: 'أخبرني عن فكرتك'        },
  company:    { en: 'We\'re hiring — tell me more', ar: 'نحن نوظف — أخبرني أكثر' },
  hr:         { en: 'We have an open position',   ar: 'لدينا وظيفة شاغرة'      },
  recruiter:  { en: 'I have a job opportunity',   ar: 'لدي فرصة عمل'           },
  developer:  { en: 'Let\'s build something',     ar: 'نبني شيئاً معاً'         },
  peer:       { en: 'Want to collaborate?',       ar: 'نتعاون في مشروع؟'       },
};

export function ChatWindow({ locale, onClose }: ChatWindowProps) {
  const ar = locale === 'ar';
  const tx = T[ar ? 'ar' : 'en'];

  const [input,           setInput]           = useState('');
  const [attachments,     setAttachments]     = useState<ChatAttachment[]>([]);
  const [typing,          setTyping]          = useState(false);
  const [messages,        setMessages]        = useState<ChatMessage[]>([]);
  const [sessionId,       setSessionId]       = useState<string | undefined>(undefined);
  const [suggestions,     setSuggestions]     = useState<string[]>([]);
  const [activeTool,      setActiveTool]      = useState<string | null>(null);
  const [userStartedChat, setUserStartedChat] = useState(false);
  const [showRating,      setShowRating]      = useState(false);
  const [isReturning,     setIsReturning]     = useState(false);
  const [visitorName,     setVisitorName]     = useState<string | null>(null);
  const [source,          setSource]          = useState<string | null>(null);
  const [greeting,        setGreeting]        = useState('');
  const [retrying,        setRetrying]        = useState(false);
  const [showScrollBtn,   setShowScrollBtn]   = useState(false);
  const [nameInput,       setNameInput]       = useState('');
  const [nameDismissed,   setNameDismissed]   = useState(false);
  const [showHistory,     setShowHistory]     = useState(false);
  const [history,         setHistory]         = useState<SessionSummary[]>([]);
  const [historyLoading,  setHistoryLoading]  = useState(false);
  const [retryInfo,       setRetryInfo]       = useState<{ text: string; attachments: ChatAttachment[] } | null>(null);

  const bottomRef  = useRef<HTMLDivElement>(null);
  const scrollRef  = useRef<HTMLDivElement>(null);
  const inputRef   = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { playUserSound, playBotSoundOnce, resetBotSound } = useChatSounds();
  const { read, write } = useVisitorMemory();

  // ── Restore session from localStorage on mount ────────────────────
  useEffect(() => {
    const v = read();
    const src = v.source || detectSource();

    // Increment session count and update profile
    write({
      sessionCount: v.sessionCount + 1,
      lastVisit:    new Date().toISOString(),
      source:       src,
      firstVisit:   v.firstVisit || new Date().toISOString(),
    });

    setSource(src);
    setIsReturning(v.sessionCount > 0);
    setVisitorName(v.name);

    // Restore chat if within TTL
    if (v.sessionId && v.messages.length > 0) {
      setSessionId(v.sessionId);
      setMessages(v.messages);
      setUserStartedChat(true);
    }

    // Time-of-day greeting based on visitor's local clock
    const h = new Date().getHours();
    const tod = h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
    setGreeting(ar
      ? (h < 12 ? 'صباح الخير' : h < 17 ? 'مساء الخير' : 'مساء النور')
      : `Good ${tod}`);

    inputRef.current?.focus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll only when user is already at the bottom
  useEffect(() => {
    if (!showScrollBtn) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, typing, suggestions, showRating, showScrollBtn]);

  // Track scroll distance to show/hide the scroll-to-bottom button
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowScrollBtn(el.scrollHeight - el.scrollTop - el.clientHeight > 120);
  };

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowScrollBtn(false);
  };

  // ── Save session to localStorage whenever messages change ─────────
  // Strip base64 data from attachments before persisting (keeps localStorage small)
  useEffect(() => {
    if (messages.length > 0) {
      const stripped = messages.map(m =>
        m.attachments
          ? { ...m, attachments: m.attachments.map(({ data: _d, ...meta }) => meta) }
          : m,
      );
      write({ messages: stripped, sessionId: sessionId ?? null });
    }
  }, [messages, sessionId, write]);

  // ── Send rating to backend ────────────────────────────────────────
  const handleRate = async (stars: number) => {
    write({ rated: true });
    if (!sessionId) return;
    try {
      await fetch(RATE_URL(sessionId), {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ rating: stars }),
      });
    } catch { /**/ }
  };

  // ── Save the optional visitor name (remembered across visits) ─────
  const saveName = () => {
    const clean = nameInput.trim().slice(0, 80);
    if (!clean) { setNameDismissed(true); return; }
    setVisitorName(clean);
    write({ name: clean });
    setNameDismissed(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // ── Per-response feedback (thumbs up/down, optional) ──────────────
  const handleMessageFeedback = (index: number, value: 'up' | 'down') => {
    const prevValue = messages[index]?.feedback;
    const nextValue = prevValue === value ? null : value; // click same thumb → clear

    setMessages((prev) => {
      const msg = prev[index];
      if (!msg || msg.role !== 'assistant') return prev;
      const updated = [...prev];
      updated[index] = { ...msg, feedback: nextValue ?? undefined };
      return updated;
    });

    if (!sessionId) return;
    fetch(FEEDBACK_URL(sessionId), {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ messageIndex: index, feedback: nextValue }),
    }).catch(() => { /* feedback is best-effort */ });
  };

  // ── Load the list of this visitor's previous conversations ────────
  const openHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    const vid = getStoredVisitorId();
    if (!vid) { setHistory([]); setHistoryLoading(false); return; }
    try {
      const res = await fetch(VISITOR_SESSIONS_URL(vid));
      const json = res.ok ? await res.json() : { data: [] };
      setHistory(Array.isArray(json.data) ? json.data : []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ── Resume a previous conversation ────────────────────────────────
  const loadSession = async (sid: string) => {
    try {
      // The backend requires the session's visitorId as a capability proof.
      const vid = getStoredVisitorId();
      const res = await fetch(SESSION_URL(sid), {
        headers: vid ? { 'x-visitor-id': vid } : {},
      });
      if (!res.ok) return;
      const { data } = await res.json();
      const msgs: ChatMessage[] = (data?.messages ?? []).map((m: any) => ({
        role:      m.role,
        content:   m.content,
        createdAt: m.createdAt,
        ...(m.feedback ? { feedback: m.feedback } : {}),
      }));
      setMessages(msgs);
      setSessionId(sid);
      setUserStartedChat(true);
      setSuggestions([]);
      setShowRating(false);
      setShowHistory(false);
      if (data?.visitorName) setVisitorName(data.visitorName);
      write({ sessionId: sid, messages: msgs });
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch { /* ignore */ }
  };

  // ── File select handler ───────────────────────────────────────────
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).slice(0, 3);
    if (!files.length) return;

    const readers = files.map(file => new Promise<ChatAttachment>(resolve => {
      if (file.size > 10 * 1024 * 1024) {
        resolve({ type: 'file', mimeType: file.type, name: file.name });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const b64  = (reader.result as string).split(',')[1];
        const type: ChatAttachment['type'] = file.type.startsWith('image/') ? 'image'
          : file.type.startsWith('audio/') ? 'audio' : 'file';
        resolve({ type, mimeType: file.type, data: b64, name: file.name });
      };
      reader.readAsDataURL(file);
    }));

    Promise.all(readers).then(next =>
      setAttachments(prev => [...prev, ...next].slice(0, 3)),
    );
    e.target.value = '';
  };

  // ── Start a brand-new conversation ───────────────────────────────
  const startNewChat = () => {
    setMessages([]);
    setSessionId(undefined);
    setSuggestions([]);
    setAttachments([]);
    setShowRating(false);
    setShowHistory(false);
    setRetryInfo(null);
    setUserStartedChat(false);
    write({ sessionId: null, messages: [], rated: false });
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // ── Input auto-resize ─────────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 112) + 'px';
  };

  // ── SSE stream reader (extracted so retry can call it) ────────────
  const readStream = async (
    res: Response,
    onAssistantContent: (text: string) => void,
    onInquirySubmitted: () => void,
    onDone: () => void,
    onError: (message: string) => void,
  ) => {
    const reader  = res.body!.getReader();
    const decoder = new TextDecoder();

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const lines = decoder.decode(value, { stream: true }).split('\n');
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const event = JSON.parse(line.slice(6));

          if (event.type === 'session') {
            setSessionId(event.sessionId);
          } else if (event.type === 'delta') {
            const text = event.text as string;
            // Hide the typing indicator the moment the first character arrives
            setTyping(false);
            setActiveTool(null);
            // Render every character individually with a human-like delay so
            // all responses — whether streamed token-by-token from the AI or
            // delivered as a single instant chunk (cache / greeting fast path)
            // — appear to be typed out letter by letter.
            for (const char of text) {
              onAssistantContent(char);
              await new Promise<void>(r => setTimeout(r, typingDelay(char)));
            }
            // Sound fires once the full chunk is written, not at the start
            playBotSoundOnce();
          } else if (event.type === 'tool_call') {
            setActiveTool(event.tool as string);
            if (event.tool === 'create_inquiry') onInquirySubmitted();
          } else if (event.type === 'suggestions') {
            setSuggestions(event.items as string[]);
          } else if (event.type === 'error') {
            onError(event.message as string);
            setTyping(false);
          } else if (event.type === 'done') {
            setActiveTool(null);
            onDone();
          }
        } catch { /* malformed SSE line */ }
      }
    }
  };

  // ── Main send handler with exponential-backoff retry ─────────────
  // `attsOverride` lets resend() replay the original attachments (the live
  // `attachments` state is cleared after each send).
  const sendMessage = async (text: string, attsOverride?: ChatAttachment[]) => {
    const trimmed  = text.trim();
    const sendAtts = attsOverride ?? attachments;
    if ((!trimmed && !sendAtts.length) || typing) return;

    setUserStartedChat(true);
    setRetryInfo(null);
    setSuggestions([]);
    setShowRating(false);
    setActiveTool(null);
    resetBotSound();
    playUserSound();
    setMessages((prev) => [...prev, {
      role: 'user',
      content: trimmed,
      createdAt: new Date().toISOString(),
      ...(sendAtts.length > 0 && { attachments: sendAtts }),
    }]);
    setInput('');
    setAttachments([]);
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setTyping(true);

    const MAX_RETRIES = 2;
    let attempt = 0;

    while (attempt <= MAX_RETRIES) {
      let assistantContent = '';
      let inquirySubmitted = false;

      // Remove any partial bot message left from a previous failed attempt
      if (attempt > 0) {
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          return last?.role === 'assistant' ? prev.slice(0, -1) : prev;
        });
      }

      try {
        const res = await fetch(CHAT_URL, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({
            userMessage: trimmed,
            lang:        locale,
            sessionId,
            visitorId:   getStoredVisitorId() ?? undefined,
            visitorName: visitorName ?? undefined,
            source:      source ?? undefined,
            ...(sendAtts.length > 0 && { attachments: sendAtts }),
          }),
        });

        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

        await readStream(
          res,
          (text) => {
            assistantContent += text;
            setMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last?.role === 'assistant') {
                return [...prev.slice(0, -1), { ...last, content: assistantContent }];
              }
              return [...prev, { role: 'assistant' as const, content: assistantContent, createdAt: new Date().toISOString() }];
            });
          },
          () => { inquirySubmitted = true; },
          () => {
            const currentCount = messages.length + 2;
            const v = read();
            if (currentCount >= 3 && !v.rated) setShowRating(true);
            if (inquirySubmitted) write({ hasSubmittedInquiry: true });
          },
          // Backend streamed an error event (HTTP 200 but type:"error") — drop
          // any partial bubble, show a resendable error, and stop.
          (message) => {
            setMessages((prev) => {
              const arr = prev[prev.length - 1]?.role === 'assistant' && !prev[prev.length - 1]?.content
                ? prev.slice(0, -1)
                : prev;
              return [...arr, {
                role:      'assistant' as const,
                content:   message,
                createdAt: new Date().toISOString(),
                error:     true,
              }];
            });
            setRetryInfo({ text: trimmed, attachments: sendAtts });
          },
        );

        break; // success — exit retry loop

      } catch {
        if (attempt < MAX_RETRIES) {
          attempt++;
          setRetrying(true);
          await new Promise((r) => setTimeout(r, 1000 * attempt)); // 1s, 2s
          setRetrying(false);
        } else {
          setMessages((prev) => [...prev, {
            role:      'assistant' as const,
            content:   ar ? 'حدث خطأ. يرجى المحاولة مجدداً.' : 'Something went wrong. Please try again.',
            createdAt: new Date().toISOString(),
            error:     true,
          }]);
          // Remember what failed so the user can resend it with one click.
          setRetryInfo({ text: trimmed, attachments: sendAtts });
          break;
        }
      }
    }

    setTyping(false);
  };

  // ── One-click resend of a failed message ──────────────────────────
  const resend = () => {
    if (!retryInfo || typing) return;
    const { text, attachments: atts } = retryInfo;
    setRetryInfo(null);
    // Drop the error bubble and the user turn that failed; sendMessage re-adds them.
    setMessages((prev) => {
      let arr = [...prev];
      if (arr[arr.length - 1]?.error) arr = arr.slice(0, -1);
      if (arr[arr.length - 1]?.role === 'user') arr = arr.slice(0, -1);
      return arr;
    });
    setTimeout(() => sendMessage(text, atts), 0);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  // ── Source chip ───────────────────────────────────────────────────
  const sourceChip = source ? SOURCE_CHIP[source] : null;
  const sourceLabel = sourceChip ? sourceChip[ar ? 'ar' : 'en'] : null;

  // ── Welcome screen ────────────────────────────────────────────────
  const showWelcome = !userStartedChat && messages.length === 0 && !showHistory;
  // Show the optional name prompt on the welcome screen until provided or skipped
  const showNamePrompt = showWelcome && !visitorName && !nameDismissed;

  return (
    <div
      role="dialog"
      aria-label={tx.title}
      aria-modal="true"
      dir={ar ? 'rtl' : 'ltr'}
      className="
        flex flex-col w-90 max-w-[calc(100vw-2rem)]
        h-130 max-h-[calc(100vh-8rem)]
        rounded-2xl border border-(--border) bg-(--bg) shadow-lg
        animate-scale-in overflow-hidden
      "
    >
      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-(--border) bg-(--surface) shrink-0">
        <div className="relative shrink-0">
          <Image src="/zain.png" alt="Zain" width={32} height={32} className="rounded-full object-cover" />
          <span className="absolute bottom-0 inset-e-0 w-2.5 h-2.5 rounded-full bg-green-400 border-2 border-(--surface)" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-(--text) truncate">{tx.title}</p>
          <p className="text-xs text-(--muted) truncate">{tx.subtitle}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {/* Previous chats button */}
          <button
            onClick={() => (showHistory ? setShowHistory(false) : openHistory())}
            title={tx.history}
            aria-label={tx.history}
            aria-pressed={showHistory}
            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors duration-180 cursor-pointer hover:bg-(--bg) ${
              showHistory ? 'text-primary-500' : 'text-(--muted) hover:text-(--text)'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
          {/* New chat button — only shown after conversation started */}
          {userStartedChat && (
            <button
              onClick={startNewChat}
              title={tx.newChat}
              aria-label={tx.newChat}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-(--muted) hover:text-(--text) hover:bg-(--bg) transition-colors duration-180 cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </button>
          )}
          <button
            onClick={onClose}
            aria-label={tx.close}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-(--muted) hover:text-(--text) hover:bg-(--bg) transition-colors duration-180 cursor-pointer"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Previous conversations panel ── */}
      {showHistory && (
        <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-(--text)">{tx.historyTitle}</h3>
            <button
              onClick={() => setShowHistory(false)}
              className="text-xs font-medium text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              {tx.back}
            </button>
          </div>

          {historyLoading ? (
            <p className="text-center text-xs text-(--muted) py-6 animate-pulse">…</p>
          ) : history.length === 0 ? (
            <p className="text-center text-xs text-(--muted) py-6">{tx.historyEmpty}</p>
          ) : (
            <ul className="space-y-1.5">
              {history.map((s) => (
                <li key={s.sessionId}>
                  <button
                    onClick={() => loadSession(s.sessionId)}
                    className={`
                      w-full text-start px-3 py-2 rounded-xl
                      border border-(--border) bg-(--surface)
                      hover:border-primary-500 hover:bg-primary-500/5
                      transition-colors duration-180 cursor-pointer
                      ${s.sessionId === sessionId ? 'border-primary-500/60' : ''}
                    `}
                  >
                    <p className="text-sm text-(--text) truncate">{s.preview || tx.welcomeTitle}</p>
                    <p className="text-xs text-(--muted) truncate mt-0.5">{s.lastMessage}</p>
                    <p className="text-xs text-(--muted) mt-0.5">
                      {new Date(s.updatedAt).toLocaleDateString(ar ? 'ar' : 'en', { month: 'short', day: 'numeric' })}
                      {' · '}{s.messageCount} {tx.messages}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ── Pre-chat quick suggestions ── */}
      {showWelcome && (
        <QuickSuggestions locale={locale} onSelect={(s) => { setUserStartedChat(true); sendMessage(s); }} />
      )}

      {/* ── Welcome screen ── */}
      {showWelcome && (
        <div className="flex flex-col items-center gap-2 px-4 py-3 text-center">
          <div className="relative">
            <Image src="/zain.png" alt="Zain" width={48} height={48} className="rounded-full object-cover" />
            <span className="absolute bottom-0.5 inset-e-0.5 w-3 h-3 rounded-full bg-green-400 border-2 border-(--bg)" />
          </div>
          {greeting && !isReturning && (
            <p className="text-xs text-primary-500 font-medium -mb-1">{greeting} 👋</p>
          )}
          <div>
            <h3 className="text-sm font-semibold text-(--text) leading-tight">
              {isReturning ? tx.welcomeBack(visitorName) : tx.welcomeTitle}
            </h3>
            <p className="text-xs text-(--muted) mt-0.5">{tx.welcomeDesc}</p>
          </div>

          {/* Optional name capture — shown once, then remembered across visits */}
          {showNamePrompt && (
            <div className="w-full max-w-60 flex flex-col items-center gap-1.5">
              <p className="text-xs text-(--muted)">{tx.namePrompt}</p>
              <div className="flex items-center gap-1.5 w-full">
                <input
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName(); }}
                  maxLength={80}
                  placeholder={tx.namePlaceholder}
                  aria-label={tx.namePlaceholder}
                  className="
                    flex-1 min-w-0 rounded-lg border border-(--border) bg-(--surface)
                    px-2.5 py-1.5 text-xs text-(--text) placeholder:text-(--muted)
                    focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500
                    transition-colors duration-180
                  "
                />
                <button
                  onClick={saveName}
                  disabled={!nameInput.trim()}
                  className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-primary-500 text-neutral-900 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer shrink-0"
                >
                  {tx.nameSave}
                </button>
                <button
                  onClick={() => setNameDismissed(true)}
                  className="px-1.5 py-1.5 rounded-lg text-xs text-(--muted) hover:text-(--text) transition-colors cursor-pointer shrink-0"
                >
                  {tx.nameSkip}
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap justify-center">
            {sourceLabel && !isReturning && (
              <button
                onClick={() => sendMessage(sourceLabel)}
                className="px-3 py-1 text-xs font-medium rounded-full bg-primary-500/10 text-primary-500 border border-primary-500/20 hover:bg-primary-500/20 transition-colors cursor-pointer"
              >
                {sourceLabel}
              </button>
            )}
            <a
              href="https://wa.me/201210529969"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-500 hover:bg-green-600 text-white transition-colors"
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.558 4.122 1.532 5.856L0 24l6.292-1.506A11.946 11.946 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.793 9.793 0 01-5.012-1.376l-.36-.214-3.733.893.924-3.638-.234-.374A9.765 9.765 0 012.182 12c0-5.42 4.398-9.818 9.818-9.818 5.42 0 9.818 4.398 9.818 9.818 0 5.42-4.398 9.818-9.818 9.818z"/>
              </svg>
              {tx.bookCall}
            </a>
          </div>

          {/* Opt in to a push notification when a new blog post is published */}
          <NewPostNotifyButton locale={locale} />
        </div>
      )}

      {/* ── Message list + scroll-to-bottom button ── */}
      {!showHistory && (
      <div className="relative flex-1 min-h-0">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="h-full overflow-y-auto px-3 py-4 space-y-3"
          role="log"
          aria-live="polite"
          aria-label="Chat messages"
        >
          {messages.length === 0 && userStartedChat && (
            <p className="text-center text-xs text-(--muted) mt-4">
              {ar ? 'ابدأ المحادثة...' : 'Start the conversation...'}
            </p>
          )}

          {messages.map((msg, i) => (
            <MessageBubble
              key={i}
              message={msg}
              feedbackLabels={{ up: tx.fbUp, down: tx.fbDown, thanks: tx.fbThanks }}
              onFeedback={
                msg.role === 'assistant' && !msg.error && !(typing && i === messages.length - 1)
                  ? (value) => handleMessageFeedback(i, value)
                  : undefined
              }
            />
          ))}

          {typing && <TypingIndicator tool={activeTool} />}

          {/* One-click resend after a failed reply */}
          {!typing && retryInfo && messages[messages.length - 1]?.error && (
            <div className="flex ps-9">
              <button
                onClick={resend}
                className="
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                  text-xs font-semibold
                  bg-primary-500/10 text-primary-500 border border-primary-500/20
                  hover:bg-primary-500/20 transition-colors duration-180 cursor-pointer
                "
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {tx.retry}
              </button>
            </div>
          )}

          {/* Reconnecting indicator */}
          {retrying && (
            <p className="text-center text-xs text-(--muted) py-1 animate-pulse">
              {ar ? 'إعادة الاتصال…' : 'Reconnecting…'}
            </p>
          )}

          {/* Dynamic suggestions from backend */}
          {!typing && suggestions.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1 pb-0.5">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => { setSuggestions([]); sendMessage(s); }}
                  className="
                    px-3 py-1.5 text-xs rounded-full
                    border border-(--border) text-(--muted)
                    hover:border-primary-500 hover:text-primary-500 hover:bg-primary-500/5
                    transition-colors duration-180 cursor-pointer
                  "
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Satisfaction rating */}
          {showRating && !typing && (
            <ChatRating locale={locale} onRate={handleRate} />
          )}

          <div ref={bottomRef} />
        </div>

        {/* Scroll-to-bottom button */}
        {showScrollBtn && (
          <button
            onClick={scrollToBottom}
            aria-label={ar ? 'انتقل للأسفل' : 'Scroll to bottom'}
            className="
              absolute bottom-3 inset-e-3
              w-7 h-7 rounded-full
              bg-(--surface) border border-(--border) shadow-md
              flex items-center justify-center
              text-(--muted) hover:text-(--text)
              transition-colors duration-180 cursor-pointer
            "
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </div>
      )}

      {/* ── Input ── */}
      {!showHistory && (
      <div className="px-3 py-3 border-t border-(--border) shrink-0">

        {/* Attachment previews */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {attachments.map((att, i) => (
              <div
                key={i}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-(--surface) border border-(--border) text-(--text) max-w-40"
              >
                {att.type === 'image' && att.data ? (
                  <img
                    src={`data:${att.mimeType};base64,${att.data}`}
                    alt={att.name ?? (locale === 'ar' ? 'صورة مرفقة' : 'Attached image')}
                    width={20}
                    height={20}
                    className="w-5 h-5 rounded object-cover shrink-0"
                  />
                ) : (
                  <span aria-hidden="true" className="shrink-0">
                    {att.type === 'audio' ? '🎵' : '📎'}
                  </span>
                )}
                <span className="truncate">{att.name ?? att.mimeType}</span>
                <button
                  onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                  aria-label={tx.removeAtt}
                  className="shrink-0 text-(--muted) hover:text-red-500 transition-colors cursor-pointer"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-end gap-2">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,audio/*,.pdf,.doc,.docx,.txt,.csv,.xlsx"
            multiple
            className="hidden"
            onChange={handleFileSelect}
            aria-hidden="true"
          />

          {/* Attach button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={typing || attachments.length >= 3}
            aria-label={tx.attach}
            title={tx.attach}
            className="
              w-9 h-9 rounded-xl border border-(--border) bg-(--surface)
              flex items-center justify-center shrink-0
              text-(--muted) hover:text-primary-500 hover:border-primary-500
              disabled:opacity-40 disabled:cursor-not-allowed
              transition-colors duration-180 cursor-pointer
            "
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </button>

          <textarea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKey}
            rows={1}
            maxLength={2000}
            placeholder={tx.placeholder}
            aria-label={tx.placeholder}
            className="
              flex-1 resize-none rounded-xl border border-(--border) bg-(--surface)
              px-3 py-2.5 text-sm text-(--text) placeholder:text-(--muted)
              focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500
              transition-colors duration-180 overflow-y-auto
            "
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={(!input.trim() && attachments.length === 0) || typing}
            aria-label={tx.send}
            className="
              w-9 h-9 rounded-xl bg-primary-500 text-neutral-900
              flex items-center justify-center shrink-0
              hover:bg-primary-600 active:bg-primary-700
              disabled:opacity-40 disabled:cursor-not-allowed
              transition-colors duration-180 cursor-pointer
            "
          >
            <svg className="w-4 h-4 rtl:rotate-180" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
        <div className="flex items-center justify-center mt-1.5">
          <p className="text-xs text-(--muted)">{tx.hint}</p>
          {input.length > 1800 && (
            <p className="text-xs text-(--muted)">{input.length}/2000</p>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

export default ChatWindow;
